import { ActionReducerMap, createFeatureSelector, createSelector } from '@ngrx/store';
import * as fromUserInfo from '@appStore/account/reducers/user-info.reducer';
import * as fromUserRegistration from '@appStore/account/reducers/user-registration.reducer';
import * as fromNotifications from '@appStore/account/reducers/notifications.reducer';
import * as fromSettings from '@appStore/account/reducers/settings.reducer';
import * as fromFilterViewSettings from '@appStore/account/reducers/filter-view-settings.reducer';
import * as fromError from '@appStore/account/reducers/errors.reducer';
import * as fromMessages from '@appStore/account/reducers/messages.reducer';
import * as fromUserNotificationSettings from '@appStore/account/reducers/user-notification-settings.reducer';
import * as fromUserMarketingSettings from '@appStore/account/reducers/user-marketing-settings.reducer';
import * as fromAccessRequests from '@appStore/account/reducers/access-requests.reducer';

import * as fromRoot from '../../../reducers';
import { SXError } from '@model';
import dayjs from 'dayjs';

// feature state
export interface AccountState {
  userinfo: fromUserInfo.State;
  userRegistration: fromUserRegistration.State;
  notifications: fromNotifications.State;
  settings: fromSettings.State;
  errors: fromError.State;
  messages: fromMessages.State;
  userNotificationSettings: fromUserNotificationSettings.State;
  filterViewSettings: fromFilterViewSettings.State;
  userMarketingSettings: fromUserMarketingSettings.State;
  accessRequests: fromAccessRequests.State;
}

export interface State extends fromRoot.State {
  account: AccountState;
}

// reducers
export const reducers: ActionReducerMap<AccountState> = {
  userinfo: fromUserInfo.reducer,
  userRegistration: fromUserRegistration.reducer,
  notifications: fromNotifications.reducer,
  settings: fromSettings.reducer,
  errors: fromError.reducer,
  messages: fromMessages.reducer,
  userNotificationSettings: fromUserNotificationSettings.reducer,
  filterViewSettings: fromFilterViewSettings.reducer,
  userMarketingSettings: fromUserMarketingSettings.reducer,
  accessRequests: fromAccessRequests.reducer
};

// ACCOUNT
export const getState = createFeatureSelector<AccountState>('account');

// USER-INFO

export const getUserIsLogingIn = createSelector(
  getState,
  (state: AccountState) => state.userinfo.userIsLogingIn
);

export const getUserInfoState = createSelector(
  getState,
  (state: AccountState) => state.userinfo
);
export const getUserInfo = createSelector(
  getUserInfoState,
  fromUserInfo.getUserInfo
);
export const getUserInfoLoading = createSelector(
  getUserInfoState,
  fromUserInfo.getUserInfoLoading
);
export const getUserInfoLoaded = createSelector(
  getUserInfoState,
  fromUserInfo.getUserInfoLoaded
);
export const getIsAuthorized = createSelector(
  getUserInfoState,
  fromUserInfo.getUserIsAuthorized
);
export const getUserType = createSelector(
  getUserInfoState,
  fromUserInfo.getUserType
);
export const getUserInfoUpdating = createSelector(
  getUserInfoState,
  fromUserInfo.getUserInfoUpdating
);
export const getUserInfoUpdated = createSelector(
  getUserInfoState,
  fromUserInfo.getUserInfoUpdated
);
export const getIsUserRegistered = createSelector(
  getUserInfoState,
  fromUserInfo.getIsUserRegistered
);
export const getIsUserRegisteredLoaded = createSelector(
  getUserInfoState,
  fromUserInfo.getIsUserRegisteredLoaded
);
export const getHasUserInfoExpired = createSelector(
  getUserInfoState,
  fromUserInfo.getHasUserInfoExpired
);
export const getHasUserInfoExpiredLoaded = createSelector(
  getUserInfoState,
  fromUserInfo.getHasUserInfoExpiredLoaded
);
export const getIsUserMarkedForInactivation = createSelector(
  getUserInfoState,
  fromUserInfo.getIsUserMarkedForInactivation
);
export const getIsUserMarkedForInactivationLoaded = createSelector(
  getUserInfoState,
  fromUserInfo.getIsUserMarkedForInactivationLoaded
);
export const getIsUserSetAsInactive = createSelector(
  getUserInfoState,
  fromUserInfo.getIsUserSetAsInactive
);
export const getIsUserSetAsInactiveLoaded = createSelector(
  getUserInfoState,
  fromUserInfo.getIsUserSetAsInactiveLoaded
);
export const getCanUserBeInactivated = createSelector(
  getUserInfoState,
  fromUserInfo.getCanUserBeInactivated
);
export const getCanUserBeInactivatedLoaded = createSelector(
  getUserInfoState,
  fromUserInfo.getCanUserBeInactivatedLoaded
);

export const getSetStartupApplicationLoaded = createSelector(
  getUserInfoState,
  fromUserInfo.getSetStartupApplicationLoaded
);

export const getSetStartupApplicationLoading = createSelector(
  getUserInfoState,
  fromUserInfo.getSetStartupApplicationLoading
);

// USER REGISTRATION
export const getUserRegistrationState = createSelector(
  getState,
  (state: AccountState) => state.userRegistration
);
export const getUserRegistrationSaving = createSelector(
  getUserRegistrationState,
  fromUserRegistration.getUserRegistrationSaving
);
export const getUserRegistrationSaved = createSelector(
  getUserRegistrationState,
  fromUserRegistration.getUserRegistrationSaved
);
export const getAutoUserRegistrationSaved = createSelector(
  getUserRegistrationState,
  fromUserRegistration.getAutoUserRegistrationSaved
);
export const getAutoUserRegistrationSaveFailed = createSelector(
  getUserRegistrationState,
  fromUserRegistration.getAutoUserRegistrationSaveFailed
);
export const getUserRegistrationSaveFailed = createSelector(
  getUserRegistrationState,
  fromUserRegistration.getUserRegistrationSaveFailed
);

export const getIsCustomerUserTypeValidLoaded = createSelector(
  getUserRegistrationState,
  fromUserRegistration.getIsCustomerValidLoaded
);
export const getIsCustomerUserTypeValid = createSelector(
  getUserRegistrationState,
  fromUserRegistration.getIsCustomerValid
);

export const getIsCustomerMeteringPointsValidLoaded = createSelector(
  getUserRegistrationState,
  fromUserRegistration.getIsCustomerMeteringPointsValidLoaded
);
export const getIsCustomerMeteringPointsValid = createSelector(
  getUserRegistrationState,
  fromUserRegistration.getIsCustomerMeteringPointsValid
);

export const getIsManagerCompanyOrgNumberExistingCustomerLoaded = createSelector(
  getUserRegistrationState,
  fromUserRegistration.getIsManagerCompanyOrgNumberExistingCustomerLoaded
);
export const getIsManagerCompanyOrgNumberExistingCustomer = createSelector(
  getUserRegistrationState,
  fromUserRegistration.getIsManagerCompanyOrgNumberExistingCustomer
);

export const getIsRegisteredOnSegment = createSelector(
  getUserRegistrationState,
  fromUserRegistration.getIsRegisteredOnSegment
);
export const getIsRegisteredOnSegmentLoaded = createSelector(
  getUserRegistrationState,
  fromUserRegistration.getIsRegisteredOnSegmentLoaded
);

export const getIsRegisteredOnCompanyLoaded = createSelector(
  getUserRegistrationState,
  fromUserRegistration.getIsRegisteredOnCompanyLoaded
);

export const getIsRegisteredOnCompanyValid = createSelector(
  getUserRegistrationState,
  fromUserRegistration.getIsRegisteredOnCompany
);

export const getCustomerUserType = createSelector(
  getUserRegistrationState,
  fromUserRegistration.getCustomerUserType
);
export const getCustomerUserTypeLoaded = createSelector(
  getUserRegistrationState,
  fromUserRegistration.getCustomerUserTypeLoaded
);

// ADD MANAGER COMPANY
export const getManagerCompanyCreated = createSelector(
  getUserRegistrationState,
  fromUserRegistration.getManagerCompanyCreated
);
// ADD ADDITIONAL MANAGER COMPANY
export const getAdditionalManagerCompanyCreated = createSelector(
  getUserRegistrationState,
  fromUserRegistration.getAdditionalManagerCompanyCreated
);
export const getManagerCompanyCreatedFail = createSelector(
  getUserRegistrationState,
  fromUserRegistration.getManagerCompanyCreatedFail
);
// ADD ADDITIONAL MANAGER COMPANY
export const getAdditionalManagerCompanyCreatedFail = createSelector(
  getUserRegistrationState,
  fromUserRegistration.getAdditionalManagerCompanyCreatedFail
);
// Get MANAGER COMPANY
export const getManagerCompanyLoaded = createSelector(
  getUserRegistrationState,
  fromUserRegistration.getManagerCompany
);

// USER ADDITIONAL REGISTRATION
export const getAdditionalCustomerRegistrationSaved = createSelector(
  getUserRegistrationState,
  fromUserRegistration.getAdditionalCustomerRegistrationSaved
);

export const getAdditionalCustomerRegistrationSaveFailed = createSelector(
  getUserRegistrationState,
  fromUserRegistration.getAdditionalCustomerRegistrationSaveFailed
);

// NOTIFICATIONS
export const getNotificationState = createSelector(
  getState,
  (state: AccountState) => state.notifications
);
export const getAllNotifications = createSelector(
  getNotificationState,
  fromNotifications.getAll
);
export const getAllUnreadNotifications = createSelector(
  getNotificationState,
  fromNotifications.getAllUnread
);
export const getAllReadNotifications = createSelector(
  getNotificationState,
  fromNotifications.getAllRead
);
export const getNotificationsLoaded = createSelector(
  getNotificationState,
  fromNotifications.getLoaded
);
export const getNotificationsLoading = createSelector(
  getNotificationState,
  fromNotifications.getLoading
);
export const getUnreadNotificationsCount = createSelector(
  getNotificationState,
  fromNotifications.getUnreadCount
);
export const getNotificationsCount = createSelector(
  getNotificationState,
  fromNotifications.getCount
);
export const getNotificationsCountLoaded = createSelector(
  getNotificationState,
  fromNotifications.getCountLoaded
);
export const getNotificationsIntervalLoaded = createSelector(
  getNotificationState,
  fromNotifications.getIntervalLoaded
);
export const getNotificationsIntervalLoading = createSelector(
  getNotificationState,
  fromNotifications.getIntervalLoading
);
export const getNotificationRemoved = createSelector(
  getNotificationState,
  fromNotifications.getItemRemoved
);
export const getNotificationRemoving = createSelector(
  getNotificationState,
  fromNotifications.getItemRemoving
);
export const getNotificationBulkRemoved = createSelector(
  getNotificationState,
  fromNotifications.getItemsRemoved
);
export const getNotificationBulkRemoving = createSelector(
  getNotificationState,
  fromNotifications.getItemsRemoving
);

// FilterExpandedSettings
export const getAnalyzeFilterViewSettingsState = createSelector(
  getState,
  (state: AccountState) => state.filterViewSettings
);
export const getAnalyzeFilterViewSettings = createSelector(
  getAnalyzeFilterViewSettingsState,
  fromFilterViewSettings.getAnalyzeFilterViewSettings
);
export const getAnalyzeFilterViewSettingsLoading = createSelector(
  getAnalyzeFilterViewSettingsState,
  fromFilterViewSettings.getLoading
);
export const getAnalyzeFilterViewSettingsLoaded = createSelector(
  getAnalyzeFilterViewSettingsState,
  fromFilterViewSettings.getLoaded
);

// Settings
export const getSettingsState = createSelector(
  getState,
  (state: AccountState) => state.settings
);
export const getSettings = createSelector(
  getSettingsState,
  fromSettings.getSettings
);
export const getSettingsLoading = createSelector(
  getSettingsState,
  fromSettings.getLoading
);
export const getSettingsLoaded = createSelector(
  getSettingsState,
  fromSettings.getLoaded
);
export const getAvailableWidgets = createSelector(
  getSettingsState,
  fromSettings.getAvailableWidgets
);
export const getHotWidgets = createSelector(
  getSettingsState,
  fromSettings.getHotWidgets
);
export const getColdWidgets = createSelector(
  getSettingsState,
  fromSettings.getColdWidgets
);

// Errors
export const getErrorsState = createSelector(
  getState,
  (state: AccountState) => state.errors
);
export const getErrors = createSelector(
  getErrorsState,
  fromError.getErrors
);

export const getCurrentBudgetError = createSelector(
  getErrorsState,
  (errorState): SXError => {
    if (!errorState.errors) {
      return;
    }
    let today = dayjs()
      .locale('sv')
      .subtract(10, 'seconds')
      .toDate();

    let error = errorState.errors.find(x => x.statusCode.toString() === '7' && x.timestamp > today);
    return error;
  }
);

// Messages
export const getMessageState = createSelector(
  getState,
  (state: AccountState) => state.messages
);
export const getAllMessages = createSelector(
  getMessageState,
  fromMessages.getAll
);
export const getMessagesLoaded = createSelector(
  getMessageState,
  fromMessages.getLoaded
);
export const getMessagesLoading = createSelector(
  getMessageState,
  fromMessages.getLoading
);
export const getUnreadMessagesCount = createSelector(
  getMessageState,
  fromMessages.getUnreadCount
);

// UserNotificationSettings
export const getUserNotificationSettingsState = createSelector(
  getState,
  (state: AccountState) => state.userNotificationSettings
);
export const getAllNotificationChannels = createSelector(
  getUserNotificationSettingsState,
  fromUserNotificationSettings.getAll
);
export const getLoadedNotificationChannels = createSelector(
  getUserNotificationSettingsState,
  fromUserNotificationSettings.getLoadedChannels
);
export const getLoadingNotificationChannels = createSelector(
  getUserNotificationSettingsState,
  fromUserNotificationSettings.getLoadingChannels
);
export const getUpdatedNotificationChannels = createSelector(
  getUserNotificationSettingsState,
  fromUserNotificationSettings.getUpdatedChannels
);
export const getUpdatingNotificationChannels = createSelector(
  getUserNotificationSettingsState,
  fromUserNotificationSettings.getUpdatingChannels
);

// UserMarketingSettings
export const getUserMarketingSettingsState = createSelector(
  getState,
  (state: AccountState) => state.userMarketingSettings
);
export const getUserMarketingSetting = createSelector(
  getUserMarketingSettingsState,
  fromUserMarketingSettings.getUserMarketingSetting
);
export const getLoadedUserMarketingSettings = createSelector(
  getUserMarketingSettingsState,
  fromUserMarketingSettings.getLoadedUserMarketingSettings
);
export const getLoadingUserMarketingSettings = createSelector(
  getUserMarketingSettingsState,
  fromUserMarketingSettings.getLoadingUserMarketingSettings
);


// AccessRequests
export const getAccessRequestsState = createSelector(
  getState,
  (state: AccountState) => state.accessRequests
);

export const getMyAccessRequests = createSelector(
  getAccessRequestsState,
  fromAccessRequests.getMyAccessRequests
);

export const getMyAccessRequestsLoaded = createSelector(
  getAccessRequestsState,
  fromAccessRequests.getMyAccessRequestsLoaded
);

export const getMyAccessRequestsLoading = createSelector(
  getAccessRequestsState,
  fromAccessRequests.getMyAccessRequestsLoading
);

export const getMyAccessRequestsRemoving = createSelector(
  getAccessRequestsState,
  fromAccessRequests.getMyAccessRequestsRemoving
);
export const getMyAccessRequestsRemoved = createSelector(
  getAccessRequestsState,
  fromAccessRequests.getMyAccessRequestsRemoved
);